import React, { useEffect, useMemo, useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  Breadcrumbs,
  DataTable,
  SectionCard,
  type DataTableColumn,
  type DataTableSortDirection,
} from '@enamelai/design-system'
import {
  buildMembershipMemberDetail,
  MEMBERSHIP_BENEFITS_PAGE_SIZE,
  MEMBERSHIP_BENEFITS_PAGE_SIZE_OPTIONS,
} from '@constants/Membership/membershipPage.constants'
import type {
  MembershipBenefitClaim,
  MembershipViewSource,
} from '@dataTypes/membership.type'
import { getMembershipMember } from '@store/actions/membership.action'
import { useAppDispatch } from '@store/hooks'
import MembershipUsageTile, {
  getMembershipUsagePercent,
} from './MembershipUsageTile.component'
import './Membership.css'
import './MembershipBenefitsUsage.css'

const compareTableValues = (
  left: string | number,
  right: string | number,
): number => {
  if (typeof left === 'number' && typeof right === 'number') {
    return left - right
  }

  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

const parseTableDate = (value: string): number => {
  const timestamp = Date.parse(value)
  return Number.isNaN(timestamp) ? 0 : timestamp
}

const parseTableAmount = (value: string): number => {
  const amount = Number(value.replace(/[^0-9.-]/g, ''))
  return Number.isNaN(amount) ? 0 : amount
}

const getBenefitSortValue = (
  row: MembershipBenefitClaim,
  columnId: string,
): string | number => {
  if (columnId === 'date') return parseTableDate(row.date)
  if (columnId === 'amount') return parseTableAmount(row.amount)
  if (columnId === 'procedure') return row.procedure
  return ''
}

const MembershipBenefitsUsage: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const { memberId } = useParams<{ memberId: string }>()
  const routedMember = (
    location.state as { member?: MembershipViewSource } | null
  )?.member
  const stateMember = routedMember?.id === memberId ? routedMember : undefined
  const [loadedMember, setLoadedMember] = useState<
    MembershipViewSource | undefined
  >(stateMember)
  const [memberRequestSettled, setMemberRequestSettled] = useState(!memberId)

  useEffect(() => {
    if (!memberId) return

    let cancelled = false
    void dispatch(getMembershipMember(memberId)).then((result) => {
      if (cancelled) return
      if (getMembershipMember.fulfilled.match(result)) {
        setLoadedMember(result.payload)
      }
      setMemberRequestSettled(true)
    })

    return () => {
      cancelled = true
    }
  }, [dispatch, memberId])

  const sourceMember = loadedMember ?? stateMember
  const member = sourceMember
    ? buildMembershipMemberDetail(sourceMember)
    : undefined

  const [sortColumnId, setSortColumnId] = useState<string | undefined>()
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(MEMBERSHIP_BENEFITS_PAGE_SIZE)

  const sortedClaims = useMemo(() => {
    const claims = member?.benefitClaims ?? []
    if (!sortColumnId) return claims

    const direction = sortDirection === 'desc' ? -1 : 1

    return [...claims].sort((left, right) => {
      return (
        compareTableValues(
          getBenefitSortValue(left, sortColumnId),
          getBenefitSortValue(right, sortColumnId),
        ) * direction
      )
    })
  }, [member, sortColumnId, sortDirection])

  const totalPages = Math.max(1, Math.ceil(sortedClaims.length / pageSize))
  const safePage = Math.min(currentPage, totalPages)
  const pagedClaims = useMemo(() => {
    const start = (safePage - 1) * pageSize
    return sortedClaims.slice(start, start + pageSize)
  }, [pageSize, safePage, sortedClaims])

  const columns: DataTableColumn<MembershipBenefitClaim>[] = useMemo(
    () => [
      { id: 'date', label: 'Date', sortable: true },
      { id: 'procedure', label: 'Procedure', sortable: true },
      { id: 'amount', label: 'Amount availed', sortable: true },
    ],
    [],
  )

  const handleSort = (columnId: string): void => {
    if (sortColumnId === columnId) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }

    setSortColumnId(columnId)
    setSortDirection('asc')
  }

  const handlePageSizeChange = (nextPageSize: number): void => {
    setPageSize(nextPageSize)
    setCurrentPage(1)
  }

  if (!memberId || (memberRequestSettled && !member)) {
    return <Navigate to="/membership" replace />
  }

  if (!member) return null

  const goToMembership = (): void => {
    navigate('/membership')
  }

  const goToMember = (): void => {
    navigate(`/membership/${member.id}`, {
      state: { member: sourceMember ?? member },
    })
  }

  return (
    <div className="membership-page membership-benefits">
      <header className="membership-page__header membership-page__header--start">
        <div className="membership-page__header-start">
          <Breadcrumbs
            className="membership-page__breadcrumbs"
            style={{ margin: 0, justifyContent: 'flex-start' }}
            items={[
              { label: 'Membership', onClick: goToMembership },
              { label: member.name, onClick: goToMember },
              { label: 'Benefits usage tracking' },
            ]}
            onBackClick={goToMember}
          />
        </div>
      </header>
      <div className="membership-benefits__body">
        <SectionCard
          className="membership-benefits__card"
          showTitleChevron={false}
          headerAfterTitle={
            <h3 className="ds-section-card__title membership-benefits__heading">
              <button
                type="button"
                className="membership-benefits__back"
                onClick={goToMember}
              >
                <i
                  className="mgc mgc_left_line membership-benefits__title-chevron"
                  aria-hidden
                />
                Benefits usage tracking
              </button>
            </h3>
          }
        >
          <div className="membership-benefits__usage-grid">
            {member.benefitUsage.map((item) => (
              <MembershipUsageTile
                key={item.id}
                label={item.label}
                value={`${item.used}/${item.total} used`}
                percent={getMembershipUsagePercent(item.used, item.total)}
              />
            ))}
            <MembershipUsageTile
              label="Total availed discounts"
              value={member.totalAvailedDiscounts}
              percent={member.totalAvailedDiscountsPercent}
            />
          </div>
          <DataTable
            className="membership-benefits__table"
            columns={columns}
            rows={pagedClaims}
            showActions={false}
            sortColumnId={sortColumnId}
            sortDirection={sortDirection}
            onSort={handleSort}
            pagination={{
              currentPage: safePage,
              totalPages,
              pageSize,
              pageSizeOptions: MEMBERSHIP_BENEFITS_PAGE_SIZE_OPTIONS,
              onPageChange: setCurrentPage,
              onPageSizeChange: handlePageSizeChange,
            }}
          />
        </SectionCard>
      </div>
    </div>
  )
}

export default MembershipBenefitsUsage
